// Comfortable / compact density (M-12 / PC-12), the same shape as
// lib/uiTheme.js: one attribute on <html> that styles.css reads
// (`:root[data-density="compact"]` tightens --row-py / --row-gap /
// --panel-pad), persisted per workstation.
//
// Why localStorage and not the profile: density is a per-screen preference
// (a 13" laptop wants compact, a 27" ops monitor does not) — the theme
// review (V-26) argued the opposite for THEME because two machines showed
// "two different products"; spacing does not change what the product is.
// Every storage call is wrapped: Safari private mode and a full quota throw
// on setItem, and a blocked-cookies profile throws on the getter itself.

export const DENSITY_COMFORTABLE = 'comfortable';
export const DENSITY_COMPACT = 'compact';

export const DENSITIES = Object.freeze([
  { id: DENSITY_COMFORTABLE, name: 'Comfortable' },
  { id: DENSITY_COMPACT, name: 'Compact' },
]);

const STORAGE_KEY = 'globalops.ui-density';
const CHANGE_EVENT = 'globalops:ui-density-change';

export function normalizeDensity(value) {
  return value === DENSITY_COMPACT ? DENSITY_COMPACT : DENSITY_COMFORTABLE;
}

export function getDensity() {
  if (typeof window === 'undefined') return DENSITY_COMFORTABLE;
  try {
    return normalizeDensity(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DENSITY_COMFORTABLE;
  }
}

export function applyDensity(value, { persist = true } = {}) {
  const density = normalizeDensity(value);
  if (typeof document !== 'undefined') {
    // Comfortable is the default spacing, so it is the ABSENCE of the
    // attribute — no selector in styles.css has to name it.
    if (density === DENSITY_COMPACT) document.documentElement.dataset.density = density;
    else delete document.documentElement.dataset.density;
  }
  if (typeof window !== 'undefined') {
    if (persist) {
      try { window.localStorage.setItem(STORAGE_KEY, density); } catch { /* private mode / quota: keep in-memory only */ }
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { density } }));
  }
  return density;
}

export function toggleDensity() {
  return applyDensity(getDensity() === DENSITY_COMPACT ? DENSITY_COMFORTABLE : DENSITY_COMPACT);
}

export function initializeDensity() {
  return applyDensity(getDensity(), { persist: false });
}

export function subscribeDensity(listener) {
  if (typeof window === 'undefined') return () => {};
  const handler = (event) => listener(normalizeDensity(event.detail?.density));
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}
